/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { captureReferralCode } from '@/shared/utils/referral-storage';
import { getRealLocalStorage } from '@/shared/utils/storage/localStorageWrapper';

/**
 * Saves return path for after authentication.
 * 
 * @param {string} path - Path to save
 * @returns {void}
 */
export function saveReturnPath(path: string): void {
  try {
    const currentSearch = window.location.search;
    captureReferralCode(currentSearch);
    const fullPath = currentSearch ? path + currentSearch : path;
    getRealLocalStorage().setItem("returnPath", window.location.origin + fullPath);
  } catch (error) {
    console.warn('Failed to save return path:', error);
  }
}

/**
 * Checks if user is authenticated.
 * 
 * @param {boolean} isAuthenticated - Local authenticated state
 * @param {boolean} authIsAuthenticated - Auth context authenticated state
 * @returns {boolean} True if authenticated
 */
export function isUserAuthenticated(isAuthenticated: boolean, authIsAuthenticated: boolean): boolean {
  return isAuthenticated || authIsAuthenticated;
}

/**
 * Checks if auth is still loading.
 * 
 * @param {boolean} authIsLoading - Auth context loading state
 * @param {boolean} isCheckingAuth - Local checking state
 * @returns {boolean} True if still loading
 */
export function isAuthStillLoading(authIsLoading: boolean, isCheckingAuth: boolean): boolean {
  return authIsLoading || isCheckingAuth;
}

/**
 * Handles navigation when authenticated.
 * 
 * @param {string} path - Path to navigate to
 * @param {Function} navigate - Navigate function
 * @returns {void}
 */
export function navigateWhenAuthenticated(path: string, navigate: (path: string) => void): void {
  navigate(path);
}

/**
 * Handles navigation when not authenticated.
 * 
 * @param {Function} signin - Signin function
 * @returns {void}
 */
export function navigateWhenNotAuthenticated(signin: () => void): void {
  signin();
}

/**
 * Handles navigation when auth is ready.
 * 
 * @param {string} path - Path to navigate to
 * @param {Function} navigate - Navigate function
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {Function} signin - Signin function
 * @returns {void}
 */
export function handleNavigationWhenReady(
  path: string,
  navigate: (path: string) => void,
  isAuthenticated: boolean,
  signin: () => void
): void {
  if (isAuthenticated) {
    navigateWhenAuthenticated(path, navigate);
  } else {
    navigateWhenNotAuthenticated(signin);
  }
}

/**
 * Checks if max retry attempts reached.
 * 
 * @param {number} attempts - Current attempt number
 * @param {number} maxAttempts - Maximum attempts
 * @returns {boolean} True if max reached
 */
export function isMaxAttemptsReached(attempts: number, maxAttempts: number): boolean {
  return attempts >= maxAttempts;
}

/**
 * Attempts navigation with retry logic.
 * 
 * @param {string} path - Path to navigate to
 * @param {Function} navigate - Navigate function
 * @param {number} attempts - Current attempt number
 * @param {number} maxAttempts - Maximum attempts
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {Function} signin - Signin function
 * @param {boolean} isStillLoading - Whether auth is still loading
 * @returns {void}
 */
export function tryNavigateWithRetry(
  path: string,
  navigate: (path: string) => void,
  attempts: number,
  maxAttempts: number,
  isAuthenticated: boolean,
  signin: () => void,
  isStillLoading: boolean
): void {
  if (isMaxAttemptsReached(attempts, maxAttempts)) {
    handleNavigationWhenReady(path, navigate, isAuthenticated, signin);
    return;
  }

  if (isStillLoading) {
    setTimeout(() => {
      tryNavigateWithRetry(path, navigate, attempts + 1, maxAttempts, isAuthenticated, signin, isStillLoading);
    }, 50);
    return;
  }

  handleNavigationWhenReady(path, navigate, isAuthenticated, signin);
}

/**
 * Navigate to a protected route. If user is not authenticated, redirects to signin.
 * 
 * @param {string} path - The protected route path to navigate to
 * @param {Function} navigate - React Router navigate function
 * @param {Function} signin - Signin function
 * @param {boolean} authIsLoading - Auth context loading state
 * @param {boolean} isCheckingAuth - Local checking state
 * @param {boolean} isAuthenticated - Local authenticated state
 * @param {boolean} authIsAuthenticated - Auth context authenticated state
 * @returns {void}
 */
export function navigateToProtectedRoute(
  path: string,
  navigate: (path: string) => void,
  signin: () => void,
  authIsLoading: boolean,
  isCheckingAuth: boolean,
  isAuthenticated: boolean,
  authIsAuthenticated: boolean
): void {
  saveReturnPath(path);

  const isStillLoading = isAuthStillLoading(authIsLoading, isCheckingAuth);
  const userIsAuthenticated = isUserAuthenticated(isAuthenticated, authIsAuthenticated);

  if (isStillLoading) {
    tryNavigateWithRetry(path, navigate, 0, 20, userIsAuthenticated, signin, isStillLoading);
    return;
  }

  handleNavigationWhenReady(path, navigate, userIsAuthenticated, signin);
}
